import dayjs from '@helpers/dayjs'
import { OrderScheduleType } from '@models/order'
import { toast } from '@openui'
import { useIsMobile } from 'hooks/use-is-mobile'
import { useActiveStore } from 'hooks/useActiveStore'
import { useRouter } from 'next/router'
import {
  parseAsInteger,
  parseAsNumberLiteral,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs'
import { useCart } from 'providers/cart-provider'
import { useState } from 'react'
import type { Store } from 'types/merchant-store'
import { useCurbside } from './use-curbside'
import { useDebounceState } from './use-debounced-state'
import { useDelivery } from './use-delivery'
import { usePickup } from './use-pickup'

type MapStep = 'DETAILS' | 'SCHEDULE' | 'STORE_HOURS'

export function useMap() {
  const { handleSelectDelivery } = useDelivery()
  const { handleSelectPickup } = usePickup()
  const { handleSelectCurbside } = useCurbside()
  const { setSchedule, cart } = useCart()
  const { updateActiveStoreMutation } = useActiveStore()
  const [isSelectingStore, setIsSelectingStore] = useState(false)
  const router = useRouter()
  const isMobile = useIsMobile()

  const [step, setStep] = useQueryState<MapStep>(
    'step',
    parseAsStringLiteral([
      'DETAILS',
      'SCHEDULE',
      'STORE_HOURS',
    ] as const).withDefault('DETAILS')
  )

  const [selectedFulfillmentMethod, setSelectedFulfillmentMethod] =
    useQueryState(
      'fulfillmentMethod',
      parseAsStringLiteral(['pickup', 'delivery', 'curbside']).withDefault(
        'pickup'
      )
    )

  const [selectedStore, setSelectedStore] = useQueryState<number>(
    'store',
    parseAsInteger
  )

  const [searchAddress, setSearchAddress] = useQueryState(
    'searchAddress',
    parseAsString.withDefault('')
  )

  // Add debounced version for API calls
  const debouncedSearchAddress = useDebounceState(searchAddress, 800)

  // Schedule
  const [scheduleDate, setScheduleDate] = useQueryState(
    'scheduleDate',
    parseAsString
  )

  const [scheduleStartTime, setScheduleStartTime] = useQueryState(
    'scheduleStartTime',
    parseAsString
  )

  const [scheduleEndTime, setScheduleEndTime] = useQueryState(
    'scheduleEndTime',
    parseAsString
  )

  const [center, setCenter] = useQueryState(
    'll',
    parseAsString.withOptions({ history: 'replace', shallow: true })
  )

  function handleResetSchedule() {
    setScheduleDate(null)
    setScheduleStartTime(null)
    setScheduleEndTime(null)
  }

  async function handleSetSchedule(storeId: number) {
    if (scheduleDate && scheduleStartTime && scheduleEndTime) {
      return await setSchedule.mutateAsync(
        {
          date: scheduleDate,
          startTime: scheduleStartTime,
          endTime: scheduleEndTime,
          scheduleType: OrderScheduleType.fixedTime,
          storeId,
        },
        {
          onError: error => {
            throw error
          },
        }
      )
    }

    await setSchedule.mutateAsync({
      scheduleType: OrderScheduleType.asap,
      date: dayjs().format('YYYY-MM-DD'),
      startTime: '00:00:00',
      endTime: '00:00:00',
      storeId,
    })
  }

  const handleOrderFromStore = async (store: Store) => {
    if (!cart.data)
      return router.push('/login', {
        query: {
          fulfillmentMethod: selectedFulfillmentMethod,
        },
      })

    try {
      setIsSelectingStore(true)
      await updateActiveStoreMutation.mutateAsync(store.id)
      await handleSetSchedule(store.id)

      if (selectedFulfillmentMethod === 'pickup') {
        await handleSelectPickup(store.slug)
      }

      if (selectedFulfillmentMethod === 'curbside') {
        await handleSelectCurbside(store.slug)
      }

      if (selectedFulfillmentMethod === 'delivery') {
        await handleSelectDelivery(store.slug)
      }

      await router.push(`/${store.slug}`)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to select store'

      toast({
        title: 'Failed to select store',
        description: message,
        type: 'error',
      })
    } finally {
      setIsSelectingStore(false)
    }
  }

  function handleSelectStore(store: Store) {
    setSelectedStore(store.id)

    if (isMobile) {
      const latlng = store.address.latlng
      setCenter(`${latlng.lat},${latlng.lng}`)

      return
    }

    setCenter(null)
  }

  function handleSchedule(store: Store) {
    if (!cart.data)
      return router.push('/login', {
        query: {
          fulfillmentMethod: selectedFulfillmentMethod,
        },
      })

    handleSelectStore(store)
    handleResetSchedule()
    setStep('SCHEDULE')
  }

  return {
    selectedFulfillmentMethod,
    setSelectedFulfillmentMethod,
    selectedStore,
    setSelectedStore,
    searchAddress,
    setSearchAddress,
    debouncedSearchAddress,
    step,
    setStep,

    handleOrderFromStore,
    handleSelectStore,
    isSelectingStore,

    scheduleDate,
    setScheduleDate,
    scheduleStartTime,
    setScheduleStartTime,
    scheduleEndTime,
    setScheduleEndTime,
    handleResetSchedule,
    handleSchedule,

    center,
    setCenter,
  }
}
